import type { Rng } from '@src/core/Rng';

/**
 * Floor rules. Every floor after the first may roll one; it colours every room on the
 * grid so two floors on the same theme still play differently. Systems read the active
 * rule through `floorMod(ctx.floor.modifier)` at the few places a value is consumed.
 */
export enum FloorModifier {
  None = 0,
  /** Bigger portal coughs, thinner bodies. */
  Swarm = 1,
  /** Extra wall guns, fewer portals. */
  Fortified = 2,
  /** Every fiend moves faster. */
  Haste = 3,
  /** Kills shed extra gems; bigger prize waves. */
  Bounty = 4,
  /** Heavies come twice as often and brutes debut early. */
  Elite = 5,
  /** Cinderwyrm's patience runs short. */
  Restless = 6,
  /** Hollow wraiths ride along with every pack. */
  Haunted = 7,
  COUNT,
}

export interface FloorModifierDef {
  id: FloorModifier;
  name: string;
  /** One-line HUD blurb. */
  blurb: string;
  /** Announcer group for the floor entrance call. */
  voiceGroup: string;
  /** Portal burst size delta and live small-fry cap delta. */
  burstBonus: number;
  liveCapBonus: number;
  /** Small fry / golem / gargoyle HP scale. */
  enemyHp: number;
  /** Wall-gun count delta (before the difficulty budget) and HP scale. */
  wallGunBonus: number;
  wallGunHp: number;
  /** Portal count delta (before the difficulty budget). */
  portalBonus: number;
  /** Enemy locomotion scale (small fry, heavies; not bosses or wall guns). */
  enemySpeed: number;
  /** Chance a kill sheds an extra gem, and prize wave delta on a clear. */
  bonusGemChance: number;
  prizeWaveBonus: number;
  /** Heavies come every N spawns instead of the Config default (0 = default). */
  golemEvery: number;
  /** Brutes replace golems from this tier (0 = Config default). */
  bruteFromTier: number;
  /** Cinderwyrm stall timer scale. */
  stallScale: number;
  /** Extra burst weight for wraiths in every room (0 = none). */
  wraithWeight: number;
}

const BASE: Omit<FloorModifierDef, 'id' | 'name' | 'blurb' | 'voiceGroup'> = {
  burstBonus: 0,
  liveCapBonus: 0,
  enemyHp: 1,
  wallGunBonus: 0,
  wallGunHp: 1,
  portalBonus: 0,
  enemySpeed: 1,
  bonusGemChance: 0,
  prizeWaveBonus: 0,
  golemEvery: 0,
  bruteFromTier: 0,
  stallScale: 1,
  wraithWeight: 0,
};

export const FLOOR_MODIFIERS: readonly FloorModifierDef[] = [
  { ...BASE, id: FloorModifier.None, name: 'STANDARD BROADCAST', blurb: 'No sponsor rules tonight.', voiceGroup: '' },
  { ...BASE, id: FloorModifier.Swarm, name: 'SWARM', blurb: 'Bigger packs, thinner skins.', voiceGroup: 'floor_mod_swarm', burstBonus: 1, liveCapBonus: 4, enemyHp: 0.82 },
  { ...BASE, id: FloorModifier.Fortified, name: 'FORTIFIED', blurb: 'The walls have teeth. Fewer portals.', voiceGroup: 'floor_mod_fortified', wallGunBonus: 2, wallGunHp: 1.2, portalBonus: -1 },
  { ...BASE, id: FloorModifier.Haste, name: 'HASTE', blurb: 'Everything down here is in a hurry.', voiceGroup: 'floor_mod_haste', enemySpeed: 1.14 },
  { ...BASE, id: FloorModifier.Bounty, name: 'BOUNTY', blurb: 'Every kill pays. Bigger prize waves.', voiceGroup: 'floor_mod_bounty', bonusGemChance: 0.4, prizeWaveBonus: 3 },
  { ...BASE, id: FloorModifier.Elite, name: 'ELITE', blurb: 'Heavies twice as often. Brutes early.', voiceGroup: 'floor_mod_elite', golemEvery: 8, bruteFromTier: 3 },
  { ...BASE, id: FloorModifier.Restless, name: 'RESTLESS', blurb: 'Cinderwyrm is already circling.', voiceGroup: 'floor_mod_restless', stallScale: 0.6 },
  { ...BASE, id: FloorModifier.Haunted, name: 'HAUNTED', blurb: 'Wraiths ride with every pack.', voiceGroup: 'floor_mod_haunted', wraithWeight: 0.2 },
];

export function floorMod(id: number): FloorModifierDef {
  return FLOOR_MODIFIERS[id] ?? FLOOR_MODIFIERS[0];
}

/** Chance a floor past the first carries a rule at all. */
const MODIFIER_CHANCE = 0.7;

/**
 * Roll the rule for a floor. Floor 1 is always clean so the opening reads the same every
 * run; later floors avoid repeating the previous rule. Rules that need a guest the tier has
 * not unlocked yet are still allowed (Haunted brings wraiths early on purpose).
 */
export function rollFloorModifier(rng: Rng, floor: number, previous: FloorModifier): FloorModifier {
  if (floor <= 1) return FloorModifier.None;
  if (!rng.chance(MODIFIER_CHANCE)) return FloorModifier.None;
  const pool: FloorModifier[] = [];
  for (let m = FloorModifier.None + 1; m < FloorModifier.COUNT; m++) {
    if (m === previous) continue;
    // Restless is a nuisance rule; keep it rarer than the rest.
    if (m === FloorModifier.Restless && rng.chance(0.5)) continue;
    pool.push(m as FloorModifier);
  }
  if (pool.length === 0) return FloorModifier.None;
  return pool[rng.int(0, pool.length - 1)];
}
